<template>
  <button
    class="vcl-button"
    :class="['primary', 'fill', 'small']"
    type="button"
    v-bind="attrs"
    :style="buttonStyles"
    v-on="listeners"
    @click="handleSelect"
  >
    <slot>
      Button
    </slot>
  </button>
</template>

<script>
export default {
  name: 'VpButton',
  inject: {
    suite: {
      default: 'unknown',
    },
  },
  props: {
    styles: {
      type: Object,
      default() {
        return {};
      },
    },
  },
  computed: {
    buttonStyles() {
      const styles = {
        '--button-color': this.styles.color || '#ffffff',
        '--button-background-color': this.styles.backgroundColor || '#016699',
        '--button-border': this.styles.border || '#016699 solid 2px',
        '--button-focus-border': this.styles.focusBorder || '#7eb0ca solid 3px',
        '--button-disabled-background-color': this.styles.disabledBackgroundColor || '#BDBDBD',
        '--button-hover-border': this.styles.hoverBorder || 'inherit',
        '--button-font-weight': this.styles.fontWeight || '700',
        '--button-text-transform': this.styles.textTransform || 'uppercase',
      };
      return styles;
    },
    listeners() {
      return this.$listeners;
    },
    attrs() {
      const { type, ...attrs } = this.$attrs;
      return attrs;
    },
  },
  methods: {
    handleSelect() {
      if (this.$gtag) {
        setTimeout(() => {
          this.$gtag.event('button_click', {
            suite: this.suite,
            button_id: this.$attrs.id !== undefined ? this.$attrs.id : 'no ID found',
          });
        }, 100);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
/* buttons */
.vcl-button {
  $self: &;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  font-style: normal;
  font-weight: var(--button-font-weight);
  letter-spacing: 0.05em;
  padding: 8px;
  margin: 0;
  text-align: center;
  text-transform: var(--button-text-transform);
  transition:  all 0.5s ease;
  & {
    background-color: var(--button-background-color);
    border: var(--button-border);
    color: var(--button-color);
  }
  &:hover {
    border: var(--button-hover-border);
    filter: brightness(115%);
  }
  &:focus {
    outline: var(--button-focus-border);
  }
  &:focus:not(:focus-visible) {
    outline: none;
  }
  &.primary {
    &:disabled {
      background-color:var(--button-disabled-background-color);
      border: 1px solid var(--button-disabled-background-color);
      cursor: not-allowed;
      opacity: 1;
      &:hover {
        filter: brightness(100%);
        background-color: var(--button-disabled-background-color);
        border: 2px solid var(--button-disabled-background-color);
      }
    }
  }
  &.secondary {
    border-radius:30px;
    &:disabled {
      background-color: var(--button-disabled-background-color);
      border: 2px solid var(--button-disabled-background-color);
      color: #fff;
      cursor:not-allowed;
      opacity: 1;
    }
    &:hover:disabled {
      background-color: var(--button-disabled-background-color);
      border: 2px solid var(--button-disabled-background-color);
    }
  }
  &.no-fill {
    color: var(--button-background-color);
    background-color: transparent;
  }
  &.blue {
    background-color: #016699;
    border: 2px solid #016699;
    color: #fff;
    &:focus {
      outline: #7eb0ca solid 3px;
    }
    &:focus:not(:focus-visible) {
      outline: none;
    }
    &:hover, &:active {
      filter: brightness(100%);
      background-color: #0B7DB7;
      border: 2px solid #0B7DB7;
    }
    &.no-fill {
      background-color: transparent;
      box-sizing:border-box;
      color:#016699;
      &:hover, &:active {
        filter: brightness(100%);
        border: 2px solid #0B7DB7;
        color: #0B7DB7;
      }
      &:disabled {
        border: 2px solid var(--button-disabled-background-color);
        color: var(--button-disabled-background-color);
        opacity: 1;
        &:hover {
          filter: brightness(100%);
          background-color:transparent;
        }
      }
    }
  }
  &.orange {
    border: 2px solid #F58220;
    background-color: #F58220;
    color: #fff;
    &:focus {
      outline: #f8bf8e solid 3px;
    }
    &:focus:not(:focus-visible) {
      outline: none;
    }
    &:hover, &:active {
      filter: brightness(100%);
      background-color: #FF9438;
      border:2px solid #FF9438;
    }
    &.no-fill {
      background-color: transparent;
      box-sizing: border-box;
      color:#F58220;
      &:hover, &:active {
        filter: brightness(100%);
        border: 2px solid #FF9438;
        color: #FF9438;
      }
      &:disabled {
        border: 2px solid var(--button-disabled-background-color);
        color: var(--button-disabled-background-color);
        opacity: 1;
        &:hover {
          filter: brightness(100%);
          border: 2px solid var(--button-disabled-background-color);
          background-color: transparent;
          color: var(--button-disabled-background-color);
        }
      }
    }
  }
  &.red {
    border: 2px solid #CE3C31;
    background-color: #CE3C31;
    color: #fff;
    &:focus {
      outline: #E49b96 solid 3px;
    }
    &:focus:not(:focus-visible) {
      outline: none;
    }
    &:hover, &:active {
      filter: brightness(100%);
      background-color: #DB4035;
      border:2px solid #DB4035;
    }
    &.no-fill {
      background-color:#fff;
      border: 2px solid #CE3C31;
      box-sizing:border-box;
      color:#CE3C31;
      &:hover, &:active {
        filter: brightness(100%);
        border: 2px solid #DB4035;
        color: #DB4035;
      }
      &:disabled {
        border: 2px solid var(--button-disabled-background-color);
        color: var(--button-disabled-background-color);
        opacity: 1;
        &:hover {
          filter: brightness(100%);
          border: 2px solid var(--button-disabled-background-color);
          background-color: transparent;
          color: var(--button-disabled-background-color);
        }
      }
    }
  }
  &.small {
    font-size: 13px;
    height: 30px;
    line-height: 11px;
    min-width: 160px;
  }
  &.medium {
    font-size: 14px;
    height: 40px;
    min-width: 143px;
  }
  &.large {
    font-size: 14px;
    height: 50px;
    min-width: 270px;
  }
}
</style>
